import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  Paper,
  Tab,
  Tabs,
  TextField,
  Divider,
} from "@mui/material";
import {
  Close,
  Save,
  Medication,
  Science,
  Description,
  InfoOutlined,
  Assignment,
} from "@mui/icons-material";
import {
  PrescriptionTemplate,
  type VetProtocolQuestionnaireItem,
} from "@/fluxvet/hooks/usePrescriptionTemplates";
import VetProtocolQuestionnairesPicker from "./VetProtocolQuestionnairesPicker";
import { TreatmentsSection } from "./TreatmentsSection";
import { ExamsProceduresSection } from "./ExamsProceduresSection";
import { ReportsSection } from "./ReportsSection";
import { GuidelinesSection } from "./GuidelinesSection";
import { toast } from "react-toastify";

// =============================================================================
// TIPOS
// =============================================================================

interface TemplateEditorProps {
  open: boolean;
  onClose: () => void;
  template: PrescriptionTemplate | null;
  onSave: (
    id: string,
    updates: {
      name: string;
      description?: string;
      treatments?: any[];
      examsOrProcedures?: any[];
      reports?: any[];
      guidelines?: string;
      questionnaires?: VetProtocolQuestionnaireItem[];
    }
  ) => void;
}

/**
 * Interface do formulário de edição de protocolo
 * Mesma estrutura do IVetAttendanceForm, mas simplificada
 */
interface TemplateEditForm {
  name: string;
  description: string;
  treatments: Array<{
    id: string;
    name: string;
    dosage: string;
    description: string;
  }>;
  examsOrProcedures: Array<{
    id: string;
    description: string;
    notes: string;
  }>;
  reports: Array<{
    id: string;
    content: string;
  }>;
  guidelines: string;
  /** Itens `type: "questionnaire"` persistidos no protocolo (FHIR template). */
  questionnaires: VetProtocolQuestionnaireItem[];
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Editor completo de protocolos de prescrição
 * Reaproveita os componentes de seção do PrescriptionsStep
 * Permite editar nome, descrição e todos os itens do protocolo
 */
export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  open,
  onClose,
  template,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Valores padrão do formulário
  const defaultValues: TemplateEditForm = {
    name: "",
    description: "",
    treatments: [],
    examsOrProcedures: [],
    reports: [],
    guidelines: "",
    questionnaires: [],
  };

  // Configuração do react-hook-form
  const methods = useForm<TemplateEditForm>({
    mode: "onChange",
    defaultValues,
  });

  const { control, handleSubmit, watch, setValue, reset } = methods;

  /**
   * Carrega os dados do protocolo no formulário quando o modal abre
   */
  useEffect(() => {
    if (open && template) {
      // Adiciona IDs únicos aos itens se não tiverem
      const treatmentsWithIds = (template.treatments || []).map((t, idx) => ({
        ...t,
        id: `edit-treatment-${idx}-${Date.now()}`,
      }));

      const examsWithIds = (template.examsOrProcedures || []).map((e, idx) => ({
        ...e,
        id: `edit-exam-${idx}-${Date.now()}`,
      }));

      const reportsWithIds = (template.reports || []).map((r, idx) => ({
        ...r,
        id: `edit-report-${idx}-${Date.now()}`,
      }));

      reset({
        name: template.name,
        description: template.description || "",
        treatments: treatmentsWithIds,
        examsOrProcedures: examsWithIds,
        reports: reportsWithIds,
        guidelines: template.guidelines || "",
        questionnaires: [...(template.questionnaires || [])],
      });
    }
  }, [open, template, reset]);

  /**
   * Handler para mudança de tab
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Salva as alterações do protocolo
   */
  const handleSaveTemplate = async (data: TemplateEditForm) => {
    if (!template) return;

    if (!data.name.trim()) {
      toast.error("O nome do protocolo é obrigatório");
      return;
    }

    setIsSaving(true);

    try {
      // Remove os IDs temporários dos itens antes de salvar
      const treatmentsWithoutIds = data.treatments.map(({ id, ...rest }) => rest);
      const examsWithoutIds = data.examsOrProcedures.map(({ id, ...rest }) => rest);
      const reportsWithoutIds = data.reports.map(({ id, ...rest }) => rest);

      onSave(template.id, {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        treatments: treatmentsWithoutIds.length > 0 ? treatmentsWithoutIds : undefined,
        examsOrProcedures: examsWithoutIds.length > 0 ? examsWithoutIds : undefined,
        reports: reportsWithoutIds.length > 0 ? reportsWithoutIds : undefined,
        guidelines: data.guidelines.trim() || undefined,
        questionnaires:
          data.questionnaires && data.questionnaires.length > 0
            ? data.questionnaires
            : undefined,
      });

      toast.success("Protocolo atualizado com sucesso!");
      handleClose();
    } catch (error) {
      console.error("Erro ao salvar protocolo:", error);
      toast.error("Erro ao salvar protocolo. Tente novamente.");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Fecha o modal e reseta o formulário
   */
  const handleClose = () => {
    reset(defaultValues);
    setActiveTab(0);
    onClose();
  };

  if (!template) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Save sx={{ color: "#00A884" }} />
            <Typography variant="h6" fontWeight={600}>
              Editar Protocolo: {template.name}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose} disabled={isSaving}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormProvider {...methods}>
          <Box component="form" sx={{ width: "100%" }}>
            <Stack spacing={3}>
              {/* Informações básicas do protocolo */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="subtitle2" fontWeight={600} color="#00A884">
                    Informações do Protocolo
                  </Typography>
                  <TextField
                    fullWidth
                    label="Nome do protocolo *"
                    placeholder="Ex: Protocolo Dermatite Atópica"
                    {...methods.register("name")}
                    size="small"
                    error={!watch("name").trim()}
                    helperText={!watch("name").trim() ? "Nome é obrigatório" : ""}
                  />
                  <TextField
                    fullWidth
                    label="Descrição"
                    placeholder="Ex: Protocolo padrão para casos de dermatite atópica leve a moderada"
                    {...methods.register("description")}
                    size="small"
                    multiline
                    rows={2}
                  />
                </Stack>
              </Paper>

              <Divider />

              {/* Seções de itens (mesmas do PrescriptionsStep) */}
              <Paper sx={{ width: "100%", minHeight: 550 }}>
                {/* Tabs de navegação entre seções */}
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTab-root": {
                      minHeight: 64,
                      textTransform: "none",
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                    "& .Mui-selected": {
                      color: "#00A884 !important",
                      fontWeight: 600,
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#00A884",
                      height: 3,
                    },
                  }}
                >
                  <Tab icon={<Medication />} iconPosition="start" label="Tratamentos" />
                  <Tab icon={<Science />} iconPosition="start" label="Exames/Procedimentos" />
                  <Tab icon={<Description />} iconPosition="start" label="Relatórios" />
                  <Tab icon={<Assignment />} iconPosition="start" label="Questionários" />
                  <Tab icon={<InfoOutlined />} iconPosition="start" label="Orientações" />
                </Tabs>

                {/* Conteúdo da tab ativa */}
                <Box sx={{ p: 3, minHeight: 470 }}>
                  {/* Tab 0: Tratamentos */}
                  {activeTab === 0 && (
                    <TreatmentsSection control={control} watch={watch} setValue={setValue} />
                  )}

                  {/* Tab 1: Exames ou Procedimentos */}
                  {activeTab === 1 && (
                    <ExamsProceduresSection control={control} watch={watch} setValue={setValue} />
                  )}

                  {/* Tab 2: Relatórios */}
                  {activeTab === 2 && (
                    <ReportsSection control={control} watch={watch} setValue={setValue} />
                  )}

                  {/* Tab 3: Questionários de pós-consulta */}
                  {activeTab === 3 && (
                    <VetProtocolQuestionnairesPicker
                      value={watch("questionnaires") || []}
                      onChange={(next) =>
                        setValue("questionnaires", next, { shouldDirty: true })
                      }
                    />
                  )}

                  {/* Tab 4: Orientações */}
                  {activeTab === 4 && <GuidelinesSection control={control} />}
                </Box>
              </Paper>
            </Stack>
          </Box>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(handleSaveTemplate)}
          variant="contained"
          startIcon={<Save />}
          disabled={isSaving || !watch("name").trim()}
          sx={{
            bgcolor: "#00A884",
            "&:hover": { bgcolor: "#008c6e" },
          }}
        >
          {isSaving ? "Salvando..." : "Salvar Alterações"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditor;
